/* import __COLOCATED_TEMPLATE__ from './cx-score-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';

interface Signature {
  Args: {
    reportState: ReportState;
  };
}

export default class CxScoreSection extends Component<Signature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  get cxScoreObjects() {
    return [
      {
        label: '5',
        barColorClass: 'bg-vis-violet-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-5',
        ),
      },
      {
        label: '4',
        barColorClass: 'bg-vis-violet-40',
        gapAfter: true,
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-4',
        ),
      },
      {
        label: '3',
        barColorClass: 'bg-vis-orange-70',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-3',
        ),
      },
      {
        label: '2',
        barColorClass: 'bg-vis-orange-60',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-2',
        ),
      },
      {
        label: '1',
        barColorClass: 'bg-vis-orange-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-1',
        ),
      },
    ];
  }

  get positiveObjects() {
    return {
      'Positive product feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-product-feedback',
        ),
        barColorClass: 'bg-vis-violet-10',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-product-feedback',
        ),
      },
      'High Fin answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-fin-answer-quality',
        ),
        barColorClass: 'bg-vis-violet-20',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-high-fin-answer-quality',
        ),
      },
      'Positive user emotion': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-emotion',
        ),
        barColorClass: 'bg-vis-violet-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-user-emotion',
        ),
      },
      'Positive policy feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-policy-feedback',
        ),
        barColorClass: 'bg-vis-violet-40',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-policy-feedback',
        ),
      },
      'Low user effort': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-effort',
        ),
        barColorClass: 'bg-vis-violet-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-low-user-effort',
        ),
      },
      'High teammate answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-teammate-answer-quality',
        ),
        barColorClass: 'bg-vis-violet-60',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-high-teammate-answer-quality',
        ),
      },
    };
  }

  get negativeObjects() {
    return {
      'Negative product feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-product-feedback',
        ),
        barColorClass: 'bg-vis-orange-20',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-product-feedback',
        ),
      },
      'Low Fin answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-fin-answer-quality',
        ),
        barColorClass: 'bg-vis-orange-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-low-fin-answer-quality',
        ),
      },
      'Negative user emotion': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-emotion',
        ),
        barColorClass: 'bg-vis-orange-40',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-user-emotion',
        ),
      },
      'Negative policy feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-policy-feedback',
        ),
        barColorClass: 'bg-vis-orange-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-policy-feedback',
        ),
      },
      'High user effort': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-effort',
        ),
        barColorClass: 'bg-vis-orange-60',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-high-user-effort',
        ),
      },
      'Low teammate answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-teammate-answer-quality',
        ),
        barColorClass: 'bg-vis-orange-70',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-low-teammate-answer-quality',
        ),
      },
    };
  }

  get cxScoreChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [{ metric_id: 'conversation.ai_generated_metrics.csat_score' }],
    };
  }

  get cxScoreBreakdownChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'ai_generated_metrics.csat',
      stacked: true,
      chart_series: [
        {
          metric_id:
            'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
          aggregation: 'count',
          show_data_labels: false,
        },
      ],
    };
  }
  get positiveCxScoreReasonsChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'conversation_custom_fields#customer_experience_attributes',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              {
                type: 'category',
                data: {
                  property: 'conversation_custom_fields#customer_experience_attributes',
                  attribute: 'conversation_custom_fields#customer_experience_attributes',
                  values: [
                    'product_feedback:positive',
                    'policy_feedback:positive',
                    'user_strong_emotion:positive',
                    'user_effort:low',
                    'answer_quality_bot:high',
                    'answer_quality_teammate:high',
                  ],
                },
              },
              {
                type: 'category',
                data: {
                  property: 'ai_generated_metrics.csat',
                  attribute: 'conversation.ai_generated_metrics.csat',
                  values: ['4', '5'],
                },
              },
            ],
          },
        },
      ],
    };
  }
  get negativeCxScoreReasonsChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'conversation_custom_fields#customer_experience_attributes',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              {
                type: 'category',
                data: {
                  property: 'ai_generated_metrics.csat',
                  attribute: 'conversation.ai_generated_metrics.csat',
                  values: ['1', '2'],
                },
              },
              {
                type: 'category',
                data: {
                  property: 'conversation_custom_fields#customer_experience_attributes',
                  attribute: 'conversation_custom_fields#customer_experience_attributes',
                  values: [
                    'product_feedback:negative',
                    'policy_feedback:negative',
                    'user_strong_emotion:negative',
                    'user_effort:high',
                    'answer_quality_bot:low',
                    'answer_quality_teammate:low',
                  ],
                },
              },
            ],
          },
        },
      ],
    };
  }

  @action
  redirectToCxScoreReport() {
    this.router.transitionTo('apps.app.reports.views.view', 'my-reports', {
      queryParams: {
        showTemplateId: 'ai_csat',
      },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxScoreSection': typeof CxScoreSection;
    'reporting/bespoke/fin-performance/cx-score-section': typeof CxScoreSection;
  }
}
